/**
 * kb-lib.ts — Hàm thuần cho trang Kho tri thức (test được, không phụ thuộc React).
 */
import { currentLocale, i18n } from '@/lib/i18n'
import type { KnowledgeCategory } from '@/hooks/use-knowledge'

export const KNOWLEDGE_BASES_PATH = '/ai/knowledge'

export type KbTab = 'docs' | 'qa' | 'materials'
export const KB_TABS: KbTab[] = ['docs', 'qa', 'materials']

/** Quyền thao tác trong Kho tri thức (knowledge.create / update / delete) — thiếu quyền thì KHÔNG hiện nút tương ứng. */
export type KnowledgePerms = { canCreate: boolean; canUpdate: boolean; canDelete: boolean }

export function knowledgeBaseUrl(id: string, tab?: KbTab): string {
  return `${KNOWLEDGE_BASES_PATH}/${encodeURIComponent(id)}${tab ? `?tab=${tab}` : ''}`
}

/** Trang Kho tri thức mở sẵn hộp "Tạo kho" (`?tao=1`), có thể điền sẵn tên (`&ten=`). Dùng khi mở từ màn khác ở tab mới. */
export function newKnowledgeBaseUrl(name?: string | null): string {
  const n = name?.trim()
  return `${KNOWLEDGE_BASES_PATH}?tao=1${n ? `&ten=${encodeURIComponent(n.slice(0, 100))}` : ''}`
}

export function emptyBaseText(): string {
  return i18n.t('aiKnow:lib.emptyBase')
}

type Counts = { articleCount?: number | null; qaCount?: number | null; materialCount?: number | null }

export function isBaseEmpty(b: Counts): boolean {
  return (b.articleCount ?? 0) + (b.qaCount ?? 0) + (b.materialCount ?? 0) === 0
}

/** "x tài liệu · y hỏi đáp · z ảnh và video" — hoặc câu nhắc khi kho trống. */
export function baseCountsLabel(b: Counts): string {
  if (isBaseEmpty(b)) return emptyBaseText()
  const n = (v?: number | null) => (v ?? 0).toLocaleString(currentLocale())
  return i18n.t('aiKnow:lib.counts', { articles: n(b.articleCount), qa: n(b.qaCount), materials: n(b.materialCount) })
}

/** Link cũ /ai?tab=knowledge&category=<id> → kho tương ứng (id kho hoặc id danh mục gốc cũ). */
export function legacyBaseTarget(categoryId: string | null | undefined, bases: Array<{ id: string; legacyCategoryId?: string | null }>): string | null {
  if (!categoryId) return null
  const hit = bases.find((b) => b.id === categoryId) ?? bases.find((b) => b.legacyCategoryId === categoryId)
  return hit?.id ?? null
}

export type CategoryNode = KnowledgeCategory & { depth: number; children: CategoryNode[] }

/** Cây danh mục của kho (theo sortOrder). Danh mục mồ côi (cha không còn) lên cấp 1. */
export function buildCategoryTree(cats: KnowledgeCategory[]): CategoryNode[] {
  const byId = new Map<string, CategoryNode>()
  for (const c of cats) byId.set(c.id, { ...c, depth: 0, children: [] })
  const roots: CategoryNode[] = []
  for (const n of byId.values()) {
    const parent = n.parentId ? byId.get(n.parentId) : undefined
    if (parent && parent.id !== n.id) parent.children.push(n)
    else roots.push(n)
  }
  const sort = (list: CategoryNode[], depth: number, seen: Set<string>) => {
    list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'vi'))
    for (const n of list) {
      if (seen.has(n.id)) { n.children = []; continue }
      seen.add(n.id)
      n.depth = depth
      sort(n.children, depth + 1, seen)
    }
  }
  sort(roots, 0, new Set())
  return roots
}

/** Duỗi cây thành danh sách theo thứ tự hiển thị. */
export function flattenTree(nodes: CategoryNode[]): CategoryNode[] {
  return nodes.flatMap((n) => [n, ...flattenTree(n.children)])
}

/** Id danh mục + mọi danh mục con cháu (lọc bảng theo một nhánh). */
export function descendantIds(cats: KnowledgeCategory[], rootId: string): string[] {
  const out = [rootId]
  for (let i = 0; i < out.length; i++) {
    for (const c of cats) if (c.parentId === out[i] && !out.includes(c.id)) out.push(c.id)
  }
  return out
}

/** Kéo thả: chuyển phần tử `from` tới vị trí của `to` (cùng cấp). */
export function moveId(ids: string[], from: string, to: string): string[] {
  if (from === to) return ids
  const next = ids.filter((x) => x !== from)
  const at = next.indexOf(to)
  if (at < 0) return ids
  const fromIdx = ids.indexOf(from)
  const toIdx = ids.indexOf(to)
  next.splice(fromIdx < toIdx ? at + 1 : at, 0, from)
  return next
}

export const MATERIAL_KIND_LABELS: Record<string, string> = {
  get image() { return i18n.t('aiKnow:lib.materialKind.image') },
  get video() { return i18n.t('aiKnow:lib.materialKind.video') },
  get audio() { return i18n.t('aiKnow:lib.materialKind.audio') },
  get file() { return i18n.t('aiKnow:lib.materialKind.file') },
}

export const MATERIAL_ACCEPT = 'image/*,video/mp4,video/quicktime,video/webm,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip'
export const MATERIAL_MAX_BYTES = 25 * 1024 * 1024

/** Kiểm tra tệp ảnh và video trước khi tải (null = hợp lệ). */
export function checkMaterialFile(f: { name: string; size: number }): string | null {
  if (f.size > MATERIAL_MAX_BYTES) return i18n.t('aiKnow:lib.fileTooBig')
  if (!/\.(jpe?g|png|gif|webp|mp4|mov|webm|mp3|m4a|wav|ogg|aac|pdf|docx?|xlsx?|pptx?|txt|csv|zip)$/i.test(f.name)) return i18n.t('aiKnow:lib.fileBadFormat')
  return null
}

/** Mã ngắn hiển thị ở cột ID (8 ký tự đầu của id). */
export function shortId(id: string): string {
  return id.replace(/-/g, '').slice(0, 8).toUpperCase()
}

// ── Tab Tài liệu: nhóm trạng thái, nguồn, thống kê, lọc + sắp xếp ─────────────

/** Nhóm trạng thái cho bộ lọc / thẻ thống kê. */
export type DocStatusGroup = 'ready' | 'keyword' | 'busy' | 'issue' | 'off'
export type DocSourceKind = 'file' | 'link' | 'manual' | 'other'
export type DocSort = 'updated_desc' | 'updated_asc' | 'name_asc' | 'chunks_desc'

export const DOC_STATUS_LABELS: Record<DocStatusGroup, string> = {
  get ready() { return i18n.t('aiKnow:lib.docStatus.ready') },
  get keyword() { return i18n.t('aiKnow:lib.docStatus.keyword') },
  get busy() { return i18n.t('aiKnow:lib.docStatus.busy') },
  get issue() { return i18n.t('aiKnow:lib.docStatus.issue') },
  get off() { return i18n.t('aiKnow:lib.docStatus.off') },
}
export const DOC_SOURCE_LABELS: Record<DocSourceKind, string> = {
  get file() { return i18n.t('aiKnow:lib.docSource.file') },
  get link() { return i18n.t('aiKnow:lib.docSource.link') },
  get manual() { return i18n.t('aiKnow:lib.docSource.manual') },
  get other() { return i18n.t('aiKnow:lib.docSource.other') },
}
export const DOC_SORT_LABELS: Record<DocSort, string> = {
  get updated_desc() { return i18n.t('aiKnow:lib.docSort.updated_desc') },
  get updated_asc() { return i18n.t('aiKnow:lib.docSort.updated_asc') },
  get name_asc() { return i18n.t('aiKnow:lib.docSort.name_asc') },
  get chunks_desc() { return i18n.t('aiKnow:lib.docSort.chunks_desc') },
}

type DocLike = { status: string; embedStatus?: string | null; sourceMeta?: { kind?: string } | null; title?: string | null; chunkCount?: number | null; updatedAt: string }

/** Tắt (AI không dùng) được ưu tiên trước trạng thái xử lý. */
export function docStatusGroup(e: DocLike): DocStatusGroup {
  if (e.status === 'archived') return 'off'
  switch (e.embedStatus) {
    case 'ready': return 'ready'
    case 'no_key': return 'keyword'
    case 'failed': case 'paused': case 'none': return 'issue'
    default: return 'busy'
  }
}

export function docSourceKind(e: DocLike): DocSourceKind {
  const k = e.sourceMeta?.kind
  if (!k) return 'manual'
  if (k === 'file' || k === 'doc_asset') return 'file'
  if (k === 'web' || k === 'gdoc') return 'link'
  return 'other'
}

export function docStats(list: DocLike[]) {
  const s = { total: list.length, inUse: 0, ready: 0, keyword: 0, busy: 0, issue: 0, off: 0, chunks: 0 }
  for (const e of list) {
    const g = docStatusGroup(e)
    s[g]++
    if (g !== 'off') s.inUse++
    s.chunks += e.chunkCount ?? 0
  }
  return s
}

export function filterSortDocs<T extends DocLike>(list: T[], f: { status: DocStatusGroup | 'all'; source: DocSourceKind | 'all'; sort: DocSort }): T[] {
  const out = list.filter((e) => (f.status === 'all' || docStatusGroup(e) === f.status) && (f.source === 'all' || docSourceKind(e) === f.source))
  const t = (e: DocLike) => new Date(e.updatedAt).getTime()
  const cmp: Record<DocSort, (a: DocLike, b: DocLike) => number> = {
    updated_desc: (a, b) => t(b) - t(a),
    updated_asc: (a, b) => t(a) - t(b),
    name_asc: (a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'vi'),
    chunks_desc: (a, b) => (b.chunkCount ?? 0) - (a.chunkCount ?? 0),
  }
  return [...out].sort(cmp[f.sort])
}

// ── Trình đọc tài liệu (danh sách + đoạn) ─────────────────────────────────────

export type DocFileKind = 'pdf' | 'word' | 'excel' | 'web' | 'text'

/** Loại biểu tượng của tài liệu theo nguồn / đuôi tệp. */
export function docFileKind(e: { sourceMeta?: { kind?: string; fileName?: string | null } | null }): DocFileKind {
  const m = e.sourceMeta
  if (m?.kind === 'web' || m?.kind === 'gdoc') return 'web'
  const ext = (/\.([a-z0-9]+)$/i.exec(m?.fileName ?? '')?.[1] ?? '').toLowerCase()
  if (ext === 'pdf') return 'pdf'
  if (ext === 'docx' || ext === 'doc') return 'word'
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return 'excel'
  return 'text'
}

/** Màu chấm trạng thái ở danh sách tài liệu. */
export const DOC_DOT_CLASS: Record<DocStatusGroup, string> = {
  ready: 'bg-success', keyword: 'bg-warning', busy: 'bg-primary animate-pulse', issue: 'bg-destructive', off: 'bg-muted-foreground/40',
}

/** Nhãn huy hiệu ở đầu trình đọc: sẵn sàng → "AI đã học xong"; còn lại dùng nhãn ngắn chung (embedStatusInfo). */
export function readerStatusLabel(e: { embedStatus?: string | null; status: string }, short: string): string {
  if (e.status === 'archived') return i18n.t('aiKnow:lib.readerOff')
  return e.embedStatus === 'ready' ? i18n.t('aiKnow:lib.readerDone') : short
}

/** Dòng đầu đoạn: "Đoạn 3/12 · <tiêu đề mục> · ~N token". */
export function chunkHeaderLine(c: { index: number; heading: string | null; tokenEstimate: number }, total: number): string {
  return [i18n.t('aiKnow:lib.chunkNumber', { n: c.index + 1, total }), c.heading?.trim() || null, i18n.t('aiKnow:lib.chunkTokens', { n: c.tokenEstimate.toLocaleString(currentLocale()) })].filter(Boolean).join(' · ')
}

const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/**
 * Tệp Word (.doc) của toàn văn: HTML có khai báo Office — Word / LibreOffice / Google Docs mở được, không cần thư viện
 * (frontend chưa có gói `docx`). Mỗi đoạn một <p>; tiêu đề mục (#) thành <h2>.
 */
export function buildWordHtml(title: string, chunks: Array<{ heading: string | null; content: string }>): string {
  const body: string[] = []
  let lastHeading: string | null = null
  for (const c of chunks) {
    if (c.heading && c.heading !== lastHeading && c.heading !== title) { body.push(`<h2>${escHtml(c.heading)}</h2>`); lastHeading = c.heading }
    for (const para of c.content.split(/\n{2,}/)) {
      const t = para.trim()
      if (!t) continue
      const md = /^#{1,6}\s+(.*)$/.exec(t)
      body.push(md ? `<h2>${escHtml(md[1])}</h2>` : `<p>${escHtml(t).replace(/\n/g, '<br/>')}</p>`)
    }
  }
  return [
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">',
    `<head><meta charset="utf-8"><title>${escHtml(title)}</title>`,
    '<style>body{font-family:Calibri,Arial,sans-serif;font-size:12pt;line-height:1.5}h1{font-size:18pt}h2{font-size:14pt}</style></head>',
    `<body><h1>${escHtml(title)}</h1>${body.join('')}</body></html>`,
  ].join('')
}

/** Tên tệp an toàn từ tiêu đề (giữ chữ có dấu, bỏ ký tự cấm của hệ điều hành). */
export function safeFileName(title: string, ext: string): string {
  const base = title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100) || 'tai-lieu'
  return `${base}.${ext}`
}
